import { useEffect, useMemo, useState } from "react";
import { sunboundPayProxy } from '@proxies';
import { useSpinnerContext, useSessionContext } from '@contexts';
import { useNavigate } from 'react-router-dom';
import { IconButton, PayorCard, Tabs, Tab, Button } from '@common';
import { ChevronLeftIcon } from "@icons";
import { Plus } from "lucide-react";
import type { AccountPayor } from '@app-types';

type TabId = `active` | `deleted`;

const ManagePayors = () => {
    const { setSpinnerShow } = useSpinnerContext();
    const { session } = useSessionContext();
    const navigate = useNavigate();
    const [accountPayors, setAccountPayors] = useState<AccountPayor[]>([]);
    const [activeTab, setActiveTab] = useState<TabId>(`active`);
    const { idAccount, email: currentUserEmail } = session || {};

    // Filter payors by active status
    const activePayors = useMemo(
        () => accountPayors.filter(ap => ap.isActive),
        [accountPayors]
    );

    const deletedPayors = useMemo(
        () => accountPayors.filter(ap => !ap.isActive),
        [accountPayors]
    );

    // Load payors on mount
    useEffect(() => {
        (async () => {
            if (!idAccount) return;
            setSpinnerShow(true);
            try {
                const ap = await sunboundPayProxy.getAccountPayors(idAccount, { includeInactive: true });
                setAccountPayors(ap);
            } catch (error) {
                console.error(error);
            }
            setSpinnerShow(false);
        })();
    }, [idAccount, setSpinnerShow]);

    // Delete (disable) a payor
    const deletePayor = async (idPayor: number) => {
        if (!idAccount) return;
        setSpinnerShow(true);
        try {
            await sunboundPayProxy.disablePayor(idAccount, idPayor);
            const ap = await sunboundPayProxy.getAccountPayors(idAccount, { includeInactive: true });
            setAccountPayors(ap);
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    // Restore (enable) a payor
    const restorePayor = async (idPayor: number) => {
        if (!idAccount) return;
        setSpinnerShow(true);
        try {
            await sunboundPayProxy.enablePayor(idAccount, idPayor);
            const ap = await sunboundPayProxy.getAccountPayors(idAccount, { includeInactive: true });
            setAccountPayors(ap);
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    // Navigate to add payor flow
    const handleAddPayor = () => {
        navigate(`/add-payor`);
    };

    return (
        <div className="flex flex-col w-full">
            {/* Header */}
            <div className="border-b border-neutral-100 pb-6 mb-8 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <IconButton
                        variant="tertiary"
                        size="sm"
                        className="flex-shrink-0 md:btn-icon-lg"
                        onClick={() => navigate(-1)}
                        aria-label="Go back"
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px] truncate">
                        Manage Payors
                    </h1>
                </div>
                <Button
                    variant="primary"
                    size="md"
                    startIcon={<Plus />}
                    className="btn-icon-lg md:btn-text-md flex-shrink-0"
                    onClick={handleAddPayor}
                    aria-label="Add Payor"
                >
                    <span className="hidden md:inline">Payor</span>
                </Button>
            </div>

            {/* Content */}
            <div className="flex flex-col gap-4">
                {/* Tab Navigation */}
                <Tabs activeTab={activeTab} onChange={(tabId) => setActiveTab(tabId as TabId)}>
                    <Tab id="active" label="Active" />
                    <Tab id="deleted" label="Deleted" />
                </Tabs>

                {/* Active Tab */}
                {activeTab === `active` && (
                    <>
                        <p className="text-base leading-6 font-normal text-neutral-800 tracking-[0.16px]">
                            Sunbound will request monthly payments from these accounts.
                        </p>
                        <div className="flex flex-col gap-4">
                            {activePayors.map(ap => {
                                const { idPayor, isGuarantor, isEnrolled, payor } = ap;
                                const isCurrentUser = payor.email === currentUserEmail;
                                const canDelete = !isEnrolled && !isGuarantor;

                                return (
                                    <PayorCard
                                        key={idPayor}
                                        name={payor.fullName}
                                        email={payor.email}
                                        isCurrentUser={isCurrentUser}
                                        onDelete={() => deletePayor(idPayor)}
                                        disabled={!canDelete}
                                    />
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Deleted Tab */}
                {activeTab === `deleted` && (
                    <>
                        {deletedPayors.length > 0 ? (
                            <>
                                <p className="text-base leading-6 font-normal text-neutral-800 tracking-[0.16px]">
                                    These accounts cannot contribute to payments unless they are restored back to an active status.
                                </p>
                                <div className="flex flex-col gap-4">
                                    {deletedPayors.map(ap => {
                                        const { idPayor, payor } = ap;
                                        const isCurrentUser = payor.email === currentUserEmail;

                                        return (
                                            <PayorCard
                                                key={idPayor}
                                                name={payor.fullName}
                                                email={payor.email}
                                                isCurrentUser={isCurrentUser}
                                                onRestore={() => restorePayor(idPayor)}
                                            />
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <p className="text-base leading-6 font-normal text-neutral-800 tracking-[0.16px]">
                                There are no deleted Payors in your account.
                            </p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ManagePayors;
